// ---------------------------------------------------------------------------
// Mapper: ServerGrader → Grader
//
// Pure function — no React, no side effects. Co-located with useGraders
// since it's only consumed there. If other files need it, promote to a
// shared utils file.
// ---------------------------------------------------------------------------

import { GRADER_METADATA_KEYS } from '../../constants';
import type { ServerGrader } from '../../services/workflowApi.interface';

import type { Grader } from './useGraders.interface';

/**
 * Returns graders in display order: newest first by `createdAt`. Graders
 * without a `createdAt` sort to the end. Pure — returns a new array and
 * leaves the input untouched. Shared by the grader list and the grader
 * store so the auto-selected grader always matches the first row shown.
 */
export function sortGradersByNewest(graders: Grader[]): Grader[] {
  return [...graders].sort((a, b) => {
    if (!a.createdAt) return 1;
    if (!b.createdAt) return -1;
    return b.createdAt.localeCompare(a.createdAt);
  });
}

export function mapServerGrader(grader: ServerGrader): Grader {
  const canonicalId = grader.name ?? '';
  const name = grader.name ?? 'Untitled Judge';

  const spec = grader.spec;
  // Narrow the polymorphic spec union by discriminator; each grader type
  // exposes a disjoint set of fields.
  const llmSpec = spec?.graderType === 'LlmGraderSpec' ? spec : undefined;
  const numericSpec =
    spec?.graderType === 'NumericComparisonGraderSpec' ? spec : undefined;
  const latencySpec =
    spec?.graderType === 'LatencyGraderSpec' ? spec : undefined;

  const systemPrompt = llmSpec?.init?.systemPrompt ?? undefined;
  const userTemplate = llmSpec?.invoke?.userMessage ?? undefined;
  const model = llmSpec?.init?.modelName ?? undefined;
  const temperature = llmSpec?.init?.temperature ?? undefined;
  const topP = llmSpec?.init?.topP ?? undefined;
  const maxTokens = llmSpec?.init?.maxTokens ?? undefined;
  const numeric = numericSpec
    ? {
        minScale: numericSpec.init?.minScale,
        maxScale: numericSpec.init?.maxScale,
        tolerance: numericSpec.init?.tolerance,
        step: numericSpec.init?.step,
        scoreExpected: numericSpec.invoke?.scoreExpected,
        scoreActual: numericSpec.invoke?.scoreActual,
      }
    : undefined;
  const latency = latencySpec
    ? {
        metricName: latencySpec.init?.metricName,
        latencyMs: latencySpec.invoke?.latencyMs,
      }
    : undefined;
  const createdAt = grader.systemProperties?.createdDate ?? undefined;
  const createdBy = grader.systemProperties?.createdBy ?? undefined;

  const sourceGraderGenId =
    grader.metadata?.[GRADER_METADATA_KEYS.SOURCE_ENTITY_ID];
  const sourceGraderGenName =
    grader.metadata?.[GRADER_METADATA_KEYS.SOURCE_GRADER_GEN_NAME];

  return {
    id: canonicalId,
    name,
    displayName: grader.displayName ?? undefined,
    description: grader.description ?? '',
    folder: grader.metadata?.folder,
    sourcePath: grader.metadata?.sourcePath,
    sourceCheckedInAt: grader.metadata?.sourceCheckedInAt,
    graderType: grader.spec?.graderType,
    rawSpec: grader.spec,
    assets: grader.assets,
    numeric,
    latency,
    isDraft: grader.isDraft ?? false,
    systemPrompt,
    userTemplate,
    model,
    temperature,
    topP,
    maxTokens,
    variables: grader.variables,
    sourceGraderGenId,
    sourceGraderGenName,
    createdAt,
    createdBy,
  };
}
